import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import css from './ListingGroupCheckboxField.module.css';
import IconCollection from '../IconCollection/IconCollection';
import { Button, Chevron, FieldCheckbox } from '../../components';
import classNames from 'classnames';
import { FieldArray } from 'react-final-form-arrays';
import ToolTip from '../ToolTip/ToolTip';


const GroupCheckboxRenderer = ({ fields, options, columns }) => {
    return (
        <div className={css.grid} style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
            {options.map((opt, colIdx) => (
                <div key={colIdx} className={css.column}>
                    <FieldCheckbox
                        key={opt.value}
                        id={`${fields.name}_${opt.value}`}
                        name={fields.name}
                        value={opt.value}
                        label={opt.label}
                        checked={fields.value?.includes(opt.value)}
                        size="lg"
                        variant="group"
                    />
                </div>
            ))}
        </div>
    )
}

const ListingGroupCheckboxField = ({
    name,
    sectionTitle,
    showRegenerate = true,
    onRegenerate,
    regenerateDisabled,
    showToggle = true,
    toggleValue,
    enabled,
    creditsUsed,
    maxCredits,
    onCreditsInfoClick,
    showCredits = true,
    label,
    options,
    value = [],
    helperText,
    required,
    columns = 2,
    showChevron = true,
    footer,
    validate,
    generateInProgress,
    generateError,
    showEnvironmentText = false,
    ...rest
}) => {
    const showEnvironmentBanner = showEnvironmentText && value?.includes('help_the_environment');
    const [expanded, setExpanded] = useState(false);
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 767);

    useEffect(() => {
        const handleResize = () => {
            if (typeof window !== 'undefined') {
                setIsMobile(window.innerWidth <= 767);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const hiddenClass = !expanded ? css.bodyCollapsed : '';

    return (
        <div className={css.root}>
            {(sectionTitle || showRegenerate || showToggle || showChevron) && (
                <div className={css.sectionHeader}>
                    <div>
                        {sectionTitle && <span className={css.sectionTitle}>{sectionTitle}</span>}

                    </div>
                    <div className={css.sectionControls}>
                        {!isMobile && showRegenerate && (
                            <Button
                                variant="warning"
                                type="button"
                                size={"sm"}
                                className={classNames(css.regenerateBtn, hiddenClass)}
                                onClick={onRegenerate}
                                disabled={regenerateDisabled}
                            >
                                <span className={classNames(css.regenerateIcon, generateInProgress && css.spinner)}>
                                    <IconCollection name="icon_regenrate" width={16} height={16} />
                                </span> Regenerate
                            </Button>
                        )}
                        {showToggle && (
                            <div className={css.toggleWrapper}>
                                <label className={css.toggleLabel}>
                                    <FieldCheckbox
                                        id={`${name}Enabled`}
                                        name={`${name}Enabled`}
                                        value={toggleValue}
                                        label=""
                                        className={css.toggleInput}
                                    />
                                    <span className={classNames(css.toggleSlider, { [css.enabled]: enabled })}></span>
                                </label>
                                <span className={css.toggleLabelText}>Enabled</span>
                            </div>
                        )}

                        {showChevron && (
                            <button
                                type="button"
                                className={css.toggleBtn}
                                onClick={() => setExpanded(e => !e)}
                                aria-label={expanded ? 'Collapse' : 'Expand'}
                            >
                                <Chevron expanded={expanded} />
                            </button>
                        )}
                    </div>
                </div>
            )}
            {label && (
                <div className={css.label}>
                    {label}
                    {required && <span className={css.required}>*</span>}
                </div>
            )}
            <div className={classNames(css.body, expanded ? css.bodyExpanded : css.bodyCollapsed)}>

                <FieldArray component={GroupCheckboxRenderer} name={name} options={options} columns={columns} validate={validate} />
                {!!helperText && <div className={css.helperText}>{helperText}</div>}
                {footer}
                <div className={classNames(css.mobileActions, hiddenClass)}>
                    {isMobile && showRegenerate && (
                        <Button
                            variant="warning"
                            type="button"
                            size={"sm"}
                            className={classNames(css.regenerateBtn, hiddenClass)}
                            onClick={onRegenerate}
                            disabled={regenerateDisabled}
                        >
                            <span className={classNames(css.regenerateIcon, generateInProgress && css.spinner)}>
                                <IconCollection name="icon_regenrate" width={16} height={16} />
                            </span> Regenerate
                        </Button>
                    )}
                    {showCredits && <div className={css.creditsRow}>
                        <span className={css.creditsText}>{creditsUsed}/{maxCredits} credits used</span>
                        {/* {typeof onCreditsInfoClick === 'function' && (
                            <button type="button" className={css.creditsInfoBtn} onClick={onCreditsInfoClick}>
                                <IconCollection name="info_icon" width={16} height={16} />
                            </button>
                        )} */}
                        <ToolTip content={`${creditsUsed}/${maxCredits} credits used`}>
                            <button type="button" className={css.creditsInfoBtn} >
                                <IconCollection name="info_icon" width={16} height={16} />
                            </button>
                        </ToolTip>
                    </div>}
                </div>
                {showEnvironmentBanner && <div className={css.environmentHighlight}>
                    <div className={css.highlightIcon}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20.998 3V5C20.998 14.6274 15.6255 19 8.99805 19L7.0964 18.9999C7.3079 15.9876 8.24541 14.1648 10.6939 11.9989C11.8979 10.9338 11.7965 10.3189 11.2029 10.6721C7.1193 13.1016 5.09114 16.3862 5.00119 21.6302L4.99805 22H2.99805C2.99805 20.6373 3.11376 19.3997 3.34381 18.2682C3.1133 16.9741 2.99805 15.2176 2.99805 13C2.99805 7.47715 7.4752 3 12.998 3C14.998 3 16.998 4 20.998 3Z" fill="#12B76A" />
                        </svg>
                    </div>
                    <div className={css.highlightText}>
                        <strong className={css.highlightTextStrong}>Sustainability Highlight Enabled</strong><br />
                        <p className={css.highlightTextP}>You selected <em>“Help the environment”</em> — As a result <strong>“Environmental Impact”</strong> section will be added to your listing to highlight your sustainability effort.</p>
                    </div>
                </div>}
            </div>
        </div>
    );
};

ListingGroupCheckboxField.propTypes = {
    name: PropTypes.string.isRequired,
    sectionTitle: PropTypes.string,
    showRegenerate: PropTypes.bool,
    onRegenerate: PropTypes.func,
    regenerateDisabled: PropTypes.bool,
    showToggle: PropTypes.bool,
    toggleValue: PropTypes.string,
    enabled: PropTypes.bool,
    creditsUsed: PropTypes.number,
    maxCredits: PropTypes.number,
    onCreditsInfoClick: PropTypes.func,
    showCredits: PropTypes.bool,
    label: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.node.isRequired,
        })
    ).isRequired,
    value: PropTypes.arrayOf(PropTypes.string),
    helperText: PropTypes.string,
    required: PropTypes.bool,
    columns: PropTypes.number,
    showChevron: PropTypes.bool,
    footer: PropTypes.node,
    validate: PropTypes.func,
};

export default ListingGroupCheckboxField; 